import { useMemo } from 'react'

import { matchSorter } from 'match-sorter'

import type { AsyncResource } from '../../useAsyncResource'
import type { SelectOption } from '../CommonProps'

const processAsyncOptions = <T extends SelectOption<any>>(
	options: Array<T> | AsyncResource<Array<T>>,
	name: string,
	isLoading?: boolean,
) => {
	if (Array.isArray(options)) {
		if (isLoading) return { options: undefined, areOptionsLoading: true }
		return { options, areOptionsLoading: false }
	}

	if (options.status === 'errored') {
		throw new Error(
			`Failed to get options for select field ${name}: ${options.error instanceof Error ? options.error.message : options.error}`,
		)
	}

	return { options: options.value, areOptionsLoading: isLoading ?? options.status === 'loading' }
}

export const useAsyncOptions = <T extends SelectOption<any>>(
	optionsAsync: Array<T> | AsyncResource<Array<T>>,
	name: string,
	inputValue: string,
	isLoading?: boolean,
) =>
	useMemo(() => {
		const { options, areOptionsLoading } = processAsyncOptions(optionsAsync, name, isLoading)
		// When the user types in the select field, we will show these options

		const filteredOptions =
			inputValue === '' || !options
				? (options ?? [])
				: // We create a new array for options to avoid issues with immutable
					matchSorter([...options], inputValue, { keys: ['label'] })
		const filteredValues = filteredOptions.map((option) => option.value)

		return { options, areOptionsLoading, filteredOptions, filteredValues }
	}, [inputValue, name, optionsAsync, isLoading])
